import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import MenuItem from 'components/brand/Header/MenuItem';
import DealsLink from 'components/brand/Header/DealsLink';
import { ChevronDown } from 'components/svg';
import withCollapsibleMenuHandlers from 'components/brand/Header/withCollapsibleMenuHandlers';
const buildDrilldownLink = (cityDrilldownData) => {
	if (!cityDrilldownData || !cityDrilldownData.content) {
		return null;
	}

	const { content, city, state } = cityDrilldownData;
	const sections = [];
	const { amenities, unitTypes, vehicleStorage } = content;

	if (Array.isArray(amenities) && amenities.length) {
		sections.push({
			sectionTitle: 'AMENITY',
			links: amenities,
		});
	}

	if (Array.isArray(unitTypes) && unitTypes.length) {
		sections.push({
			sectionTitle: 'SIZE',
			links: unitTypes,
		});
	}

	if (Array.isArray(vehicleStorage) && vehicleStorage.length) {
		sections.push({
			sectionTitle: 'VEHICLE',
			links: vehicleStorage,
		});
	}

	if (!sections.length) {
		return null;
	}

	const locationLabel = [city, state].filter(Boolean).join(', ');

	return {
		text: 'Find Storage',
		dropdownHeader: locationLabel
			? `Find Storage Units in ${locationLabel}`
			: undefined,
		innerLinks: sections,
		isActive: false,
	};
};

const getLinksData = (city = 'Seattle', region = 'WA', cityDrilldownData) => {
	const location = `${city}, ${region}`;
	const customLink = buildDrilldownLink(cityDrilldownData);
	const links = [
		{
			text: 'Find Storage',
			innerLinks: [
				{
					sectionTitle: 'AMENITY',
					links: [
						{
							href: `/near-me/24-hour-storage.html`,
							text: `24 Hour Storage Near You`,
						},
						{
							href: `/near-me/climate-controlled-storage.html`,
							text: `Climate Controlled Storage Near You`,
						},
						{
							href: `/near-me/drive-up-storage.html`,
							text: `Drive Up Storage Near You`,
						},
						{
							href: `/near-me/storage-with-electricity.html`,
							text: `Storage With Electricity Near You`,
						},
						{
							href: `/near-me/storage-with-free-truck-rental.html`,
							text: `Storage With Free Truck Rental Near You`,
						},
						{
							href: `/near-me/wine-storage.html`,
							text: `Wine Storage Near You`,
						},
					],
				},
				{
					sectionTitle: 'SIZE',
					links: [
						{
							href: `/search.html?location=${encodeURIComponent(
								location,
							)}&sqft=25-75`,
							text: `Small Storage Unit Near You`,
						},
						{
							href: `/search.html?location=${encodeURIComponent(
								location,
							)}&sqft=76-200`,
							text: `Medium Storage Unit Near You`,
						},
						{
							href: `/search.html?location=${encodeURIComponent(
								location,
							)}&sqft=201-999`,
							text: `Large Storage Unit Near You`,
						},
						{
							href: `/near-me/mini-storage.html`,
							text: `Mini Storage Near You`,
						},
						{
							href: `/near-me/locker-storage.html`,
							text: `Storage Lockers Near You`,
						},
					],
				},
				{
					sectionTitle: 'VEHICLE',
					links: [
						{
							href: `/near-me/boat-storage.html`,
							text: `Boat Storage Near You`,
						},
						{
							href: `/near-me/car-storage.html`,
							text: `Car Storage Near You`,
						},
						{
							href: `/near-me/rv-storage.html`,
							text: `RV Storage Near You`,
						},
					],
				},
			],
			isActive: false,
		},
		{
			text: 'Storage Types',
			innerLinks: [
				{ href: '/car-storage.html', text: 'Car Storage' },
				{ href: '/rv-storage.html', text: 'RV Storage' },
				{ href: '/boat-storage.html', text: 'Boat Storage' },
				{ href: '/college-storage.html ', text: 'College Storage' },
				{ href: '/portable-storage.html', text: 'Portable Storage' },
				{ href: '/business-storage.html', text: 'Business Storage' },
				{
					href: '/climate-controlled-storage.html',
					text: 'Climate Controlled',
				},
				{ href: '/storage-101.html', text: 'Find Self-Storage' },
			],
			isActive: false,
		},
		{
			text: 'Storage 101',
			innerLinks: [
				{
					href: 'https://storageguide.sparefoot.com/self-storage-amenities/ ',
					text: 'Storage Unit Amenities',
				},
				{
					href: 'https://storageguide.sparefoot.com/the-basics/ ',
					text: 'Storage FAQ',
				},
				{
					href: 'https://storageguide.sparefoot.com/storage-unit-safety-and-security/  ',
					text: 'Storage Security',
				},
				{
					href: '/storage-unit-size-guide.html ',
					text: 'Storage Unit Size Guide',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/industry-trends/',
					text: 'Storage Beat',
				},
			],
			isActive: false,
		},
		{
			text: 'Learn',
			innerLinks: [
				{
					href: 'https://www.sparefoot.com/blog/category/general-storage-advice/',
					text: 'General Storage Advice',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/how-to-store/',
					text: 'How to Store It',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/moving-tips/',
					text: 'Moving Tips',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/getting-organized/',
					text: 'Getting Organized',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/city-guides/',
					text: 'City Guides',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/self-storage/',
					text: 'Self Storage',
				},
				{
					href: 'https://www.sparefoot.com/blog/category/industry-trends/news/',
					text: 'News',
				},
			],
			isActive: false,
		},
		{ href: 'https://storageguide.sparefoot.com/', text: 'Help Center' },
		{ href: 'https://about.sparefoot.com/ ', text: 'About Us' },
		{
			customItem: <DealsLink className="menu-secondary" />,
		},
		{
			href: 'https://marketing.sparefoot.com',
			text: 'Add Your Facility',
			rightAlign: true,
		},
	];

	return customLink ? [customLink, ...links.slice(1)] : links;
};

const mapStateToProps = (state) => ({
	geolocation: state.user.geolocation,
	drilldownContent:
		state?.search?.drilldownLinks ||
		state?.facilities?.drillDownLinks,
	drilldownCity: state.search.location.city,
	drilldownRegion: state.search.location.region,
});

export const enhance = compose(
	withRouter,
	connect(mapStateToProps),
	withCollapsibleMenuHandlers,
);
export class HeaderMenu extends PureComponent {
	constructor(props) {
		super(props);
		this.handleClickOutside = this.handleClickOutside.bind(this);
		this.handleClick = this.handleClick.bind(this);
		this.menuId = 'mobile-menu-toggle';
		this.menuToggleButtonRef = React.createRef();
		this.linkContainerRef = React.createRef();
		this.menuToggleInputRef = React.createRef();
		this.state = {
			links: this.buildLinksFromProps(props),
		};
	}
	componentDidMount() {
		document.addEventListener('mousedown', this.handleClickOutside);
		this.unlistenHistory = this.props.history?.listen((newLocation) => {
			this.setState({
				links: this.buildLinksFromProps({ ...this.props, location: newLocation }),
			});
		});
	}

	componentDidUpdate(prevProps) {
		if (
			prevProps.geolocation !== this.props.geolocation ||
			prevProps.drilldownContent !== this.props.drilldownContent
		) {
			this.setState({
				links: this.buildLinksFromProps(this.props),
			});
		}
	}

	buildLinksFromProps(props) {
		const { geolocation, drilldownContent, drilldownCity, drilldownRegion, location } = props;
		const pathname = location?.pathname || '';
		const isNearMePage = pathname.includes('/near-me/');
		const cityDrilldownLinks = isNearMePage
			? null
			: {
				content: drilldownContent,
				city: drilldownCity,
				state: drilldownRegion,
			};
		return getLinksData(
			geolocation?.city,
			geolocation?.region,
			cityDrilldownLinks,
		);
	}

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleClickOutside);
		if (this.unlistenHistory) this.unlistenHistory();
	}
	handleClickOutside(event) {
		// Only close mobile menu toggle, not individual accordions
		const element = this.menuToggleInputRef?.current;
		const menuToggleButton = this.menuToggleButtonRef?.current;
		const linkContainer = this.linkContainerRef?.current;

		// Close mobile menu only if clicking outside both toggle button and menu container
		const isOutsideMenu =
			!menuToggleButton?.contains(event.target) &&
			!linkContainer?.contains(event.target);

		if (isOutsideMenu && element?.checked) {
			element.checked = false;
		}
	}
	handleClick(text) {
		this.setState((prevState) => ({
			links: prevState.links.map((link) => {
				if (link?.text && text.includes(link?.text)) {
					return { ...link, isActive: !link.isActive };
				}
				return { ...link, isActive: false };
			}),
		}));
	}
	render() {
		return (
			<nav
				className={classnames('header-menu', {
					expanded: this.props.menuExpanded,
				})}
				ref={this.bindRef}
			>
				<div
					className="menu-toggle-button"
					ref={this.menuToggleButtonRef}
				>
					<label htmlFor="mobile-menu-toggle">
						<span className="hidden-text">Show Menu</span>
						<input
							ref={this.menuToggleInputRef}
							name="mobile-menu-toggle"
							type="checkbox"
							id="mobile-menu-toggle"
							className="visually-hidden"
						/>
						<ChevronDown />
					</label>
				</div>
				<div
					className="link-container"
					ref={this.linkContainerRef}
				>
					{this.state?.links.map((link) =>
						link.customItem ? (
							<Fragment key={link.customItem}>
								{link.customItem}
							</Fragment>
						) : (
							<MenuItem
								{...link}
								key={link.text}
								id={link.text
									?.toLowerCase()
									.replace(/\s+/g, '-')}
								handleClick={this.handleClick}
							/>
						),
					)}
				</div>
			</nav>
		);
	}
}
HeaderMenu.propTypes = {
	menuExpanded: PropTypes.bool,
	onClickOutside: PropTypes.func,
	toggleMenu: PropTypes.func,
	viewportWidth: PropTypes.number,
	geolocation: PropTypes.shape({
		city: PropTypes.string,
		region: PropTypes.string,
	}),
	drilldownContent: PropTypes.object,
	drilldownCity: PropTypes.string,
	drilldownRegion: PropTypes.string,
	location: PropTypes.shape({
		pathname: PropTypes.string,
	}),
	history: PropTypes.shape({
		listen: PropTypes.func,
	}),
};
export default enhance(HeaderMenu);
